import { Moon, Sun } from "lucide-react";

import { useTranslation } from "@/store/locale";
import { useThemeStore } from "@/store/theme";
import { cn } from "@/utils/utils";

/**
 * Light / dark, as two buttons rather than one switch.
 *
 * A single toggle has to show either the current theme or the one it will
 * switch to, and people read it both ways. Two buttons say which is selected
 * and leave nothing to interpret.
 */
export function ThemeToggle() {
  const t = useTranslation();
  const choice = useThemeStore((state) => state.choice);
  const setChoice = useThemeStore((state) => state.setChoice);
  const active = choice ?? "light";

  return (
    <div
      role="group"
      aria-label="Theme"
      className="bg-surface-raised border-line inline-flex items-center gap-0.5 rounded-full border p-0.5 shadow-[var(--shadow-lift)]"
    >
      {(
        [
          ["light", Sun, t("theme.light")],
          ["dark", Moon, t("theme.dark")],
        ] as const
      ).map(([value, Icon, label]) => (
        <button
          key={value}
          type="button"
          onClick={() => setChoice(value)}
          aria-pressed={active === value}
          title={label}
          className={cn(
            "grid size-9 place-items-center rounded-full transition-colors duration-[var(--duration-fast)]",
            active === value
              ? "bg-accent text-accent-fg"
              : "text-fg-muted hover:bg-surface-inset hover:text-fg",
          )}
        >
          <Icon className="size-4.5" aria-hidden />
          <span className="sr-only">{label}</span>
        </button>
      ))}
    </div>
  );
}
